/** Minimal element helper — enough to keep the forms readable, nothing more. */

type Child = Node | string | null | undefined | false;

interface Props {
  class?: string;
  text?: string;
  type?: string;
  value?: string;
  placeholder?: string;
  title?: string;
  min?: string;
  step?: string;
  checked?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  dataset?: Record<string, string>;
  onClick?: (event: MouseEvent) => void;
  onChange?: () => void;
}

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Props = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);

  if (props.class) el.className = props.class;
  if (props.text !== undefined) el.textContent = props.text;
  if (props.title) el.title = props.title;
  if (props.hidden) el.hidden = true;

  if (el instanceof HTMLInputElement) {
    if (props.type) el.type = props.type;
    if (props.value !== undefined) el.value = props.value;
    if (props.placeholder) el.placeholder = props.placeholder;
    if (props.min !== undefined) el.min = props.min;
    if (props.step !== undefined) el.step = props.step;
    if (props.checked !== undefined) el.checked = props.checked;
  }
  if (el instanceof HTMLButtonElement) {
    el.type = 'button';
    if (props.disabled !== undefined) el.disabled = props.disabled;
  }

  for (const [key, value] of Object.entries(props.dataset ?? {})) el.dataset[key] = value;
  if (props.onClick) el.addEventListener('click', props.onClick as EventListener);
  if (props.onChange) el.addEventListener('change', props.onChange);

  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    el.append(child);
  }

  return el;
}

/** A labelled field, matching the mockup's stacked label/input pairs. */
export function field(label: string, ...controls: Child[]): HTMLLabelElement {
  return h('label', {}, label, ...controls);
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Conventional glyphs, drawn rather than emoji so they render identically everywhere. */
const ICON_PATHS = {
  // A pencil over a sheet: "edit this entry", not "draw something".
  edit: [
    'M3 2.2h5.2l3.3 3.2V9',
    'M8.2 2.2v3.2h3.3',
    'M3 13.8V2.2',
    'M5.2 7.4h3.2',
    'M5.2 9.7h2',
    'M8.9 14.3l.5-2.1 3.8-3.8 1.6 1.6-3.8 3.8z',
  ],
  delete: ['M2.5 4.5h11', 'M6 4.5V2.5h4v2', 'M4 4.5l.8 9.5h6.4l.8-9.5', 'M6.5 7v4.5', 'M9.5 7v4.5'],
  up: ['M8 12V4', 'M4.5 7.5L8 4l3.5 3.5'],
  down: ['M8 4v8', 'M4.5 8.5L8 12l3.5-3.5'],
  close: ['M4.2 4.2l7.6 7.6', 'M11.8 4.2l-7.6 7.6'],
  filter: ['M2.5 3.5h11l-4.3 5v4.2l-2.4 1.3V8.5z'],
} as const;

const ICON_SIZE = 16;

export function icon(name: keyof typeof ICON_PATHS): SVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('width', String(ICON_SIZE));
  svg.setAttribute('height', String(ICON_SIZE));
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.3');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');

  for (const definition of ICON_PATHS[name]) {
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', definition);
    svg.append(path);
  }

  return svg;
}

/** An icon-only button: the label lives in the tooltip and the accessible name. */
export function iconButton(
  name: keyof typeof ICON_PATHS,
  label: string,
  onClick: () => void,
  extra: { class?: string; disabled?: boolean } = {},
): HTMLButtonElement {
  const button = h('button', {
    class: ['icon-button', extra.class].filter(Boolean).join(' '),
    title: label,
    disabled: extra.disabled ?? false,
  });
  button.setAttribute('aria-label', label);
  button.append(icon(name));
  button.addEventListener('click', onClick);
  return button;
}

export function option(value: string, label: string): HTMLOptionElement {
  const el = document.createElement('option');
  el.value = value;
  el.textContent = label;
  return el;
}

/** Reads a number input, returning null when empty or not a number. */
export function numberValue(input: HTMLInputElement): number | null {
  if (input.value.trim() === '') return null;
  const value = Number(input.value);
  return Number.isFinite(value) ? value : null;
}
